import { useEffect, useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import {
  clockIn,
  clockOut,
  listAttendanceLogs,
  listProjects,
  listShifts,
  listTimesheets,
  submitTimesheet,
  type AttendanceLog,
  type Project,
  type Shift,
  type Timesheet,
} from '../api/client';
import { StatusBadge } from '../components/StatusBadge';
import { Icon } from '../components/Icon';

export function Attendance() {
  const { session } = useAuth();
  const [tab, setTab] = useState<'attendance' | 'timesheets' | 'shifts'>('attendance');
  const [logs, setLogs] = useState<AttendanceLog[]>([]);
  const [timesheets, setTimesheets] = useState<Timesheet[]>([]);
  const [shifts, setShifts] = useState<Shift[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [clocking, setClocking] = useState(false);
  const [msg, setMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Punch state
  const todayStr = new Date().toISOString().slice(0, 10);
  const todayLog = logs.find((l) => l.date === todayStr);

  // Modal states
  const [regModal, setRegModal] = useState(false);
  const [regDate, setRegDate] = useState(todayStr);
  const [regIn, setRegIn] = useState('09:00');
  const [regOut, setRegOut] = useState('18:00');
  const [regReason, setRegReason] = useState('');

  const [tsModal, setTsModal] = useState(false);
  const [tsProjectId, setTsProjectId] = useState('');
  const [tsDate, setTsDate] = useState(todayStr);
  const [tsHours, setTsHours] = useState(8);
  const [tsDescription, setTsDescription] = useState('');
  const [tsBillable, setTsBillable] = useState(true);

  async function loadData() {
    try {
      const [logsRes, tsRes, shiftsRes, projRes] = await Promise.all([
        listAttendanceLogs(),
        listTimesheets().catch(() => ({ data: [] })),
        listShifts().catch(() => ({ data: [] })),
        listProjects().catch(() => ({ data: [] })),
      ]);
      setLogs(logsRes.data || []);
      setTimesheets(tsRes.data || []);
      setShifts(shiftsRes.data || []);
      setProjects(projRes.data || []);
      if (projRes.data?.length && !tsProjectId) {
        setTsProjectId(projRes.data[0].id);
      }
    } catch (err: any) {
      console.error('Failed to load attendance records:', err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadData();
  }, []);

  async function handleClockIn() {
    setClocking(true);
    setMsg(null);
    try {
      await clockIn({ notes: 'Manual Punch' });
      setMsg({ type: 'success', text: 'Clocked in successfully!' });
      await loadData();
    } catch (err: any) {
      setMsg({ type: 'error', text: err.message || 'Clock in failed' });
    } finally {
      setClocking(false);
    }
  }

  async function handleClockOut() {
    setClocking(true);
    setMsg(null);
    try {
      await clockOut();
      setMsg({ type: 'success', text: 'Clocked out successfully!' });
      await loadData();
    } catch (err: any) {
      setMsg({ type: 'error', text: err.message || 'Clock out failed' });
    } finally {
      setClocking(false);
    }
  }

  async function handleRegularize(e: React.FormEvent) {
    e.preventDefault();
    setMsg({ type: 'success', text: 'Regularization request submitted to reporting manager.' });
    setRegModal(false);
    setRegReason('');
  }

  async function handleTimesheetSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!tsProjectId) return;
    try {
      await submitTimesheet({
        projectId: tsProjectId,
        workDate: tsDate,
        hours: tsHours,
        taskDescription: tsDescription,
        isBillable: tsBillable,
      });
      setMsg({ type: 'success', text: 'Timesheet logged successfully!' });
      setTsModal(false);
      setTsDescription('');
      await loadData();
    } catch (err: any) {
      setMsg({ type: 'error', text: err.message || 'Failed to log timesheet' });
    }
  }

  const [liveTime, setLiveTime] = useState(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }));

  useEffect(() => {
    const timer = setInterval(() => {
      setLiveTime(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }));
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div>
      <div className="page-head">
        <div>
          <span className="eyebrow">
            <Icon name="attendance" size="xs" />
            <span>Operations & Time</span>
          </span>
          <h1>Attendance & Timesheets</h1>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          {tab === 'attendance' && (
            <button className="btn-quiet btn-sm" onClick={() => setRegModal(true)}>
              <Icon name="edit" size="xs" />
              <span>Regularize Punch</span>
            </button>
          )}
          {tab === 'timesheets' && (
            <button className="btn-primary btn-sm" onClick={() => setTsModal(true)}>
              <Icon name="plus" size="xs" />
              <span>Log Timesheet</span>
            </button>
          )}
        </div>
      </div>

      {msg && <div className={`banner ${msg.type}`}>{msg.text}</div>}

      {/* Clock In / Out Quick Widget (Clear Dual Actions) */}
      <div
        className="panel punch-clock-card"
        style={{
          marginBottom: 20,
          padding: '20px 22px',
          borderLeft: todayLog?.clock_in && !todayLog?.clock_out ? '4px solid var(--status-verified)' : '4px solid var(--brand-primary)',
          borderRadius: 14,
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div
              style={{
                width: 48,
                height: 48,
                borderRadius: 'var(--radius-md)',
                background: todayLog?.clock_in && !todayLog?.clock_out ? 'var(--status-verified-bg)' : 'var(--bg-field)',
                color: todayLog?.clock_in && !todayLog?.clock_out ? 'var(--status-verified)' : 'var(--brand-primary)',
                display: 'grid',
                placeItems: 'center',
                boxShadow: 'var(--shadow-sm)',
                flexShrink: 0,
              }}
            >
              <Icon name={todayLog?.clock_in && !todayLog?.clock_out ? 'clock' : 'attendance'} size="xl" />
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 5,
                    padding: '3px 8px',
                    borderRadius: 12,
                    fontSize: 10.5,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                    background: todayLog?.clock_in && !todayLog?.clock_out ? 'var(--status-verified-bg)' : 'var(--bg-field)',
                    color: todayLog?.clock_in && !todayLog?.clock_out ? 'var(--status-verified)' : 'var(--ink-muted)',
                  }}
                >
                  <Icon
                    name={todayLog?.clock_in && !todayLog?.clock_out ? 'checkCircle' : todayLog?.clock_out ? 'clockCircle' : 'infoCircle'}
                    size="xs"
                  />
                  <span>{todayLog?.clock_in && !todayLog?.clock_out ? 'On Duty · Active Shift' : todayLog?.clock_out ? 'Shift Finished' : 'Off Duty · Not Clocked In'}</span>
                </span>
                <span className="mono muted" style={{ fontSize: 11 }}>Live: {liveTime}</span>
              </div>

              <div style={{ fontSize: 15, fontWeight: 700, marginTop: 4 }}>
                {todayLog?.clock_in && !todayLog?.clock_out ? (
                  <span>
                    In Shift · Clocked In at <strong className="mono" style={{ color: 'var(--status-verified)' }}>{new Date(todayLog.clock_in).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</strong>
                  </span>
                ) : todayLog?.clock_out ? (
                  <span>
                    Shift Completed · <span className="mono">{todayLog.total_work_minutes} mins worked</span>
                  </span>
                ) : (
                  <span>Ready to start shift · General Shift (09:00 - 18:00)</span>
                )}
              </div>
            </div>
          </div>

          {/* DUAL EXPLICIT ACTION BUTTONS */}
          <div className="punch-card-actions" style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            {/* Clock In Button */}
            <button
              type="button"
              className={!todayLog?.clock_in ? 'btn-success' : 'btn-quiet'}
              onClick={() => void handleClockIn()}
              disabled={clocking || !!todayLog?.clock_in}
              style={{
                padding: '10px 18px',
                fontSize: 13,
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
                borderRadius: 10,
                cursor: !todayLog?.clock_in ? 'pointer' : 'default',
              }}
              title={todayLog?.clock_in ? `Already clocked in at ${new Date(todayLog.clock_in).toLocaleTimeString()}` : 'Start your shift'}
            >
              <Icon name="checkCircle" size="sm" />
              <span>{!todayLog?.clock_in ? 'Clock In' : `In: ${new Date(todayLog.clock_in).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`}</span>
            </button>

            {/* Clock Out Button */}
            <button
              type="button"
              className={todayLog?.clock_in && !todayLog?.clock_out ? 'btn-danger' : 'btn-quiet'}
              onClick={() => void handleClockOut()}
              disabled={clocking || !todayLog?.clock_in || !!todayLog?.clock_out}
              style={{
                padding: '10px 18px',
                fontSize: 13,
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
                borderRadius: 10,
                opacity: todayLog?.clock_in && !todayLog?.clock_out ? 1 : 0.5,
                cursor: todayLog?.clock_in && !todayLog?.clock_out ? 'pointer' : 'not-allowed',
              }}
              title={!todayLog?.clock_in ? 'You must clock in first before clocking out' : todayLog?.clock_out ? `Clocked out at ${new Date(todayLog.clock_out).toLocaleTimeString()}` : 'End your shift'}
            >
              <Icon name="clock" size="sm" />
              <span>{todayLog?.clock_out ? `Out: ${new Date(todayLog.clock_out).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : 'Clock Out'}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="tabs" style={{ marginBottom: 16 }}>
        <button className={`tab-btn ${tab === 'attendance' ? 'active' : ''}`} onClick={() => setTab('attendance')}>
          <Icon name="attendance" size="xs" />
          <span>Attendance Log</span>
        </button>
        <button className={`tab-btn ${tab === 'timesheets' ? 'active' : ''}`} onClick={() => setTab('timesheets')}>
          <Icon name="calendar" size="xs" />
          <span>Project Timesheets</span>
        </button>
        <button className={`tab-btn ${tab === 'shifts' ? 'active' : ''}`} onClick={() => setTab('shifts')}>
          <Icon name="clock" size="xs" />
          <span>Shifts & Rosters</span>
        </button>
      </div>

      {loading ? (
        <div className="panel"><div className="empty">Loading records...</div></div>
      ) : tab === 'attendance' ? (
        <div className="panel">
          <div className="panel-head">
            <strong>Attendance Register</strong>
            <span className="muted" style={{ fontSize: 12 }}>Showing recent punch records</span>
          </div>
          <div className="table-responsive">
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Employee</th>
                  <th>Clock In</th>
                  <th>Clock Out</th>
                  <th>Total Work</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {logs.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="empty">No attendance records found.</td>
                  </tr>
                ) : (
                  logs.map((l) => (
                    <tr key={l.id}>
                      <td className="mono">{l.date}</td>
                      <td>
                        <strong>{l.first_name ? `${l.first_name} ${l.last_name || ''}` : session?.user.fullName}</strong>
                        <span className="muted mono" style={{ fontSize: 11, display: 'block' }}>{l.employee_code || session?.user.email}</span>
                      </td>
                      <td className="mono">{l.clock_in ? new Date(l.clock_in).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—'}</td>
                      <td className="mono">{l.clock_out ? new Date(l.clock_out).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—'}</td>
                      <td className="mono">{l.total_work_minutes > 0 ? `${Math.floor(l.total_work_minutes / 60)}h ${l.total_work_minutes % 60}m` : '—'}</td>
                      <td>
                        <StatusBadge status={l.status} />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : tab === 'timesheets' ? (
        <div className="panel">
          <div className="panel-head">
            <strong>Project Timesheets</strong>
            <span className="muted" style={{ fontSize: 12 }}>Work logged against project codes</span>
          </div>
          <div className="table-responsive">
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Project</th>
                  <th>Task Description</th>
                  <th>Hours</th>
                  <th>Billable</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {timesheets.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="empty">No timesheets logged.</td>
                  </tr>
                ) : (
                  timesheets.map((t) => (
                    <tr key={t.id}>
                      <td className="mono">{t.work_date}</td>
                      <td>
                        <strong>{t.project_name}</strong>
                        <span className="muted mono" style={{ fontSize: 11, display: 'block' }}>{t.project_code}</span>
                      </td>
                      <td>{t.task_description}</td>
                      <td className="mono">{t.hours} hrs</td>
                      <td>{t.is_billable ? <StatusBadge status="approved" label="Billable" /> : <StatusBadge status="draft" label="Non-billable" />}</td>
                      <td><StatusBadge status={t.status} /></td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="panel">
          <div className="panel-head">
            <strong>Configured Shifts</strong>
          </div>
          <div className="table-responsive">
            <table>
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Shift Name</th>
                  <th>Timings</th>
                  <th>Break Time</th>
                  <th>Night Shift</th>
                </tr>
              </thead>
              <tbody>
                {shifts.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="empty">No shifts configured.</td>
                  </tr>
                ) : (
                  shifts.map((s) => (
                    <tr key={s.id}>
                      <td className="mono">{s.code}</td>
                      <td><strong>{s.name}</strong></td>
                      <td className="mono">{s.start_time} - {s.end_time}</td>
                      <td className="mono">{s.break_minutes} mins</td>
                      <td>{s.is_night_shift ? 'Yes' : 'No'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Regularize Modal */}
      {regModal && (
        <div className="modal-backdrop" onClick={() => setRegModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <span className="eyebrow">Attendance Correction</span>
                <h3>Request Punch Regularization</h3>
              </div>
              <button className="btn-icon" onClick={() => setRegModal(false)} aria-label="Close">
                <Icon name="close" size="sm" />
              </button>
            </div>
            <form onSubmit={handleRegularize}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div className="form-group">
                  <label>Date</label>
                  <input type="date" required value={regDate} onChange={(e) => setRegDate(e.target.value)} />
                </div>
                <div className="form-grid-2">
                  <div className="form-group">
                    <label>Requested Clock In</label>
                    <input type="time" required value={regIn} onChange={(e) => setRegIn(e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label>Requested Clock Out</label>
                    <input type="time" required value={regOut} onChange={(e) => setRegOut(e.target.value)} />
                  </div>
                </div>
                <div className="form-group">
                  <label>Reason for Missed Punch</label>
                  <textarea
                    required
                    rows={3}
                    placeholder="e.g., Client visit on-site, biometric sensor failure"
                    value={regReason}
                    onChange={(e) => setRegReason(e.target.value)}
                  />
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
                <button type="button" className="btn-quiet" onClick={() => setRegModal(false)}>Cancel</button>
                <button type="submit" className="btn-primary">Submit Request</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Timesheet Modal */}
      {tsModal && (
        <div className="modal-backdrop" onClick={() => setTsModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <span className="eyebrow">Project Allocation</span>
                <h3>Log Timesheet Entry</h3>
              </div>
              <button className="btn-icon" onClick={() => setTsModal(false)} aria-label="Close">
                <Icon name="close" size="sm" />
              </button>
            </div>
            <form onSubmit={handleTimesheetSubmit}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div className="form-group">
                  <label>Project</label>
                  <select value={tsProjectId} onChange={(e) => setTsProjectId(e.target.value)} required>
                    {projects.map((p) => (
                      <option key={p.id} value={p.id}>{p.code} - {p.name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-grid-2">
                  <div className="form-group">
                    <label>Work Date</label>
                    <input type="date" required value={tsDate} onChange={(e) => setTsDate(e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label>Hours Spent</label>
                    <input type="number" min="0.5" max="24" step="0.5" required value={tsHours} onChange={(e) => setTsHours(Number(e.target.value))} />
                  </div>
                </div>
                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                    <input type="checkbox" checked={tsBillable} onChange={(e) => setTsBillable(e.target.checked)} style={{ width: 'auto' }} />
                    Billable Hours
                  </label>
                </div>
                <div className="form-group">
                  <label>Task Summary</label>
                  <textarea
                    required
                    rows={3}
                    placeholder="Describe what was accomplished..."
                    value={tsDescription}
                    onChange={(e) => setTsDescription(e.target.value)}
                  />
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
                <button type="button" className="btn-quiet" onClick={() => setTsModal(false)}>Cancel</button>
                <button type="submit" className="btn-primary">Save Timesheet</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
